import { useMutation, useQuery } from '@tanstack/react-query';
import { ErrorNote, Loading } from '../../components/ui';
import { errorMessage } from '../../lib/api';
import { formatDateTime } from '../../lib/format';
import { jobs as jobsApi } from '../../lib/resources';
import type { Job } from '../../types';

const TYPE_LABELS: Record<Job['type'], string> = {
  IMPORT: 'Importação',
  EXPORT: 'Exportação',
  BULK_DELETE: 'Exclusão em massa',
};

const STATUS: Record<Job['status'], [string, string]> = {
  QUEUED: ['Na fila', 'bg-slate-100 text-slate-600'],
  RUNNING: ['Em execução', 'bg-sky-50 text-sky-700'],
  DONE: ['Concluída', 'bg-emerald-50 text-emerald-700'],
  FAILED: ['Falhou', 'bg-red-50 text-red-700'],
};

const TOTAL_LABELS: Record<string, string> = {
  rows: 'linhas',
  created: 'criados',
  updated: 'atualizados',
  conflicts: 'conflitos',
  failed: 'falhas',
  deleted: 'excluídos',
  skipped: 'ignorados',
  exported: 'exportados',
};

/** Configurações › Execuções — importações, exportações e exclusões em massa. */
export function JobsSection() {
  const query = useQuery({
    queryKey: ['jobs'],
    queryFn: jobsApi.list,
    // Enquanto alguma roda, atualiza sozinho.
    refetchInterval: (current) =>
      current.state.data?.data.some(
        (job) => job.status === 'QUEUED' || job.status === 'RUNNING',
      )
        ? 2000
        : false,
  });
  const download = useMutation({
    mutationFn: ({ id, name }: { id: string; name: string }) => jobsApi.download(id, name),
  });

  return (
    <section className="rounded-xl border border-slate-200 bg-white">
      <header className="border-b border-slate-200 px-5 py-4">
        <h2 className="text-base font-semibold text-slate-900">Execuções</h2>
        <p className="text-xs text-slate-500">
          Processos em segundo plano, com progresso, falhas e arquivos.
        </p>
      </header>
      {download.error && (
        <div className="px-5 pt-3">
          <ErrorNote>{errorMessage(download.error)}</ErrorNote>
        </div>
      )}
      {query.isLoading ? (
        <Loading />
      ) : (query.data?.data ?? []).length === 0 ? (
        <p className="px-5 py-10 text-center text-sm text-slate-400">Nenhuma execução ainda.</p>
      ) : (
        <ul className="divide-y divide-slate-100">
          {query.data!.data.map((job) => {
            const [statusLabel, statusClass] = STATUS[job.status];
            const percent =
              job.total > 0
                ? Math.round((job.progress / job.total) * 100)
                : job.status === 'DONE'
                  ? 100
                  : 0;
            return (
              <li key={job.id} className="px-5 py-4 text-sm">
                <div className="flex flex-wrap items-center gap-2">
                  <span className="font-medium text-slate-800">
                    {TYPE_LABELS[job.type]} · {job.entity === 'leads' ? 'Leads' : job.entity}
                  </span>
                  <span className={`rounded-full px-2 py-0.5 text-xs ${statusClass}`}>
                    {statusLabel}
                  </span>
                  <span className="ml-auto text-xs text-slate-400">
                    {job.createdByName ?? 'Sistema'} · {formatDateTime(job.createdAt)}
                  </span>
                </div>
                <div className="mt-2 h-1.5 rounded-full bg-slate-100">
                  <div
                    className="h-1.5 rounded-full"
                    style={{ width: `${percent}%`, backgroundColor: 'var(--crm-primary)' }}
                  />
                </div>
                <p className="mt-1 text-xs text-slate-500">
                  {job.progress}/{job.total}
                  {Object.entries(job.totals).map(
                    ([key, value]) => ` · ${value} ${TOTAL_LABELS[key] ?? key}`,
                  )}
                </p>
                {job.error && <p className="mt-1 text-xs text-red-600">{job.error}</p>}
                {job.files.length > 0 && (
                  <div className="mt-2 flex flex-wrap gap-2">
                    {job.files.map((file) => (
                      <button
                        key={file.name}
                        type="button"
                        className="rounded-lg border border-slate-200 px-2 py-1 text-xs text-slate-700 hover:border-slate-400"
                        onClick={() => download.mutate({ id: job.id, name: file.name })}
                      >
                        ⬇ {file.label} ({Math.max(1, Math.round(file.size / 1024))} KB)
                      </button>
                    ))}
                  </div>
                )}
              </li>
            );
          })}
        </ul>
      )}
    </section>
  );
}
